import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Alert,
  Linking,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useStyle} from 'src/providers/style';
import addressFormat from 'src/common-utils/address-format';
import {Typography} from 'src/styles';

type Address = {
  fullAddress?: string;
  location?: {latitude?: number; longitude?: number};
  placeId?: string;
  streetLine?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  secondLine?: string;
};

interface Props {
  address?: Address | null;
  showIcon?: boolean;
  iconSize?: number;
  showHeader?: boolean;
  testID?: string;
}

const MapsLink: React.FC<Props> = ({
  address,
  showIcon = true,
  iconSize = 22,
  showHeader = true,
  testID,
}) => {
  const styles = useStyle();

  const fullAddress = (address?.fullAddress ?? '').trim();
  const lat = Number(address?.location?.latitude);
  const lng = Number(address?.location?.longitude);
  const placeId = address?.placeId;

  const derived = (addressFormat(address || {}) ?? '').trim();
  const labelText = fullAddress || derived;
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

  if (!labelText && !hasCoords) {
    return null;
  }

  const safeOpenURL = (url: string) =>
    Linking.openURL(url).catch(() => {
      const w = globalThis as any;
      if (Platform.OS === 'web' && w?.open) {
        try {
          w.open(url, '_blank');
          return;
        } catch {}
      }
      Alert.alert(
        "Couldn't open Maps",
        'Please try again or install a maps app.',
      );
    });

  const openInAppleMaps = () => {
    const url = hasCoords
      ? `http://maps.apple.com/?ll=${lat},${lng}&q=${encodeURIComponent(
          labelText,
        )}`
      : `http://maps.apple.com/?q=${encodeURIComponent(labelText)}`;
    return safeOpenURL(url);
  };

  const buildGMapsUniversalLink = () => {
    const base = 'https://www.google.com/maps/search/?api=1';
    if (placeId) {
      const query = hasCoords ? `${lat},${lng}` : encodeURIComponent(labelText);
      return `${base}&query=${query}&query_place_id=${placeId}`;
    }
    return hasCoords
      ? `${base}&query=${lat},${lng}`
      : `${base}&query=${encodeURIComponent(labelText)}`;
  };

  const openInGoogleMaps = async () => {
    const universal = buildGMapsUniversalLink();
    if (Platform.OS === 'ios') {
      try {
        const canUseAppWrapper = await Linking.canOpenURL(
          'comgooglemapsurl://',
        );
        if (canUseAppWrapper) {
          const appUrl =
            'comgooglemapsurl://' + universal.replace(/^https?:\/\//, '');
          return safeOpenURL(appUrl);
        }
      } catch {
        /* fall through */
      }
    }
    return safeOpenURL(universal);
  };

  const openOnAndroid = () => {
    if (placeId) {
      return safeOpenURL(buildGMapsUniversalLink());
    } // geo: doesn't support place_id
    const enc = encodeURIComponent(
      labelText || (hasCoords ? `${lat},${lng}` : ''),
    );
    const url = hasCoords
      ? `geo:0,0?q=${lat},${lng}(${enc})`
      : `geo:0,0?q=${enc}`;
    return safeOpenURL(url);
  };

  const onPress = async () => {
    if (Platform.OS === 'web') {
      const g = globalThis as any;
      const ok = g?.confirm
        ? g.confirm(`Open in Google Maps?\n\n${labelText}`)
        : true;
      if (ok) {
        await openInGoogleMaps();
      }
      return;
    }

    if (Platform.OS === 'ios') {
      let googleSupported = false;
      try {
        googleSupported = await Linking.canOpenURL('comgooglemaps://');
      } catch {}

      if (googleSupported) {
        Alert.alert('Open in Maps', labelText, [
          {text: 'Apple Maps', onPress: openInAppleMaps},
          {text: 'Google Maps', onPress: openInGoogleMaps},
          {text: 'Cancel', style: 'cancel'},
        ]);
      } else {
        Alert.alert('Open in Apple Maps?', labelText, [
          {text: 'Open', onPress: openInAppleMaps},
          {text: 'Cancel', style: 'cancel'},
        ]);
      }
      return;
    }

    // Android
    Alert.alert('Open in Maps?', labelText, [
      {text: 'Open', onPress: openOnAndroid},
      {text: 'Cancel', style: 'cancel'},
    ]);
  };

  return (
    <>
      {showHeader && (
        <Text style={[Typography.H6, styles.marginSMTop]}>Address</Text>
      )}

      <TouchableOpacity
        onPress={onPress}
        accessibilityRole="link"
        accessibilityLabel={`Open ${labelText || 'address'} in maps`}
        testID={testID || 'maps-link'}
        style={[styles.row, styles.alignCenter]}
        hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
        delayPressIn={0}>
        {showIcon && (
          <View style={[styles.icon]}>
            <Icon name="map-marker" size={iconSize} />
          </View>
        )}
        <Text
          style={[
            Typography.LABEL_MEDIUM,
            styles.textColorLink,
            styles.flexWrap,
            styles.flexShrink,
          ]}
          numberOfLines={2}
          ellipsizeMode="tail">
          {labelText}
        </Text>
      </TouchableOpacity>
    </>
  );
};

export default MapsLink;
